import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Divider,
  CircularProgress
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Visibility";
import { extractTextFromPdf, generateVideoScript } from "../../apis/apiList/openAiSciptApi";
import { useParams } from "react-router-dom";
import { getNewsletterByOrgId } from "../../apis/apiList/newsletterApi";
import { downloadFileFromBox } from "../../apis/apiList/boxAssetsApi";
import { format } from "date-fns";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  height: "90vh",
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  overflow: "hidden",
  display: "flex",
};

const NewsletterScriptModal = ({ open, onClose, newsletters = [], userDetails, handleAddScriptInPageInput }) => {
  const [selectedNewsletters, setSelectedNewsletters] = useState([]);
  const [previewText, setPreviewText] = useState(null);
  const [parsedText, setParsedText] = useState("");
  const [step, setStep] = useState("select");
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [newslettersList, setNewslettersList] = useState([]);
  const [promptLoading, setPromptLoading] = useState(false);
  const [finalLoading, setFinalLoading] = useState(false);
  const [generatedScript, setGeneratedScript] = useState("");

  useEffect(() => {
    async function fetchNewsletters() {
      try {
        const res = await getNewsletterByOrgId(id, {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": userDetails.jwtToken,
          },
        });
        setNewslettersList(res.newsletters);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching newsletters:", error);
      }
    }
    fetchNewsletters();
  }, []);

  const handleToggleSelect = (id) => {
    setSelectedNewsletters((prev) =>
      prev.includes(id) ? prev.filter((nid) => nid !== id) : [...prev, id]
    );
  };

  const handlePreview = (content) => {
    setPreviewText(content);
  };

  const handleGenerateScript = async () => {
    try {
      setPromptLoading(true);
      const script = await extractTextFromPdf({ newsletterIds: selectedNewsletters });
      setParsedText(script.extractedText || "");
      setStep("edit");
      setPromptLoading(false);
    } catch (error) {
      console.error("Error generating script:", error);
      setPromptLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const file = await downloadFileFromBox(attachment?.boxFileId);
      if (file?.data) {
        const link = document.createElement("a");
        link.href = file?.data;
        link.download = "";
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("File URL not found");
      }
    } catch (error) {
      console.error("Failed to download attachment:", error);
    }
  };

  const handleGenerateFinalScript = async () => {
    try {
      setFinalLoading(true);
      console.log("Generating final script with parsed text:", parsedText);
      const script = await generateVideoScript({ prompt: parsedText });
      console.log("Generated final script:", script);
      setGeneratedScript(script?.solution || "");
      setStep("edit");
      setFinalLoading(false);
    } catch (error) {
      console.error("Error generating script:", error);
      setFinalLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {step === "select" ? (
          <LeftPanel
            newsletters={newslettersList}
            selectedNewsletters={selectedNewsletters}
            onToggle={handleToggleSelect}
            onGenerate={handleGenerateScript}
            previewText={previewText}
            onPreview={handlePreview}
            handleDownloadAttachment={handleDownloadAttachment}
            onResumeEdit={() => setStep("edit")}
            isResumeAvailable={parsedText !== ""}
            promptLoading={promptLoading}
            onClose={onClose}
          />
        ) : (
          <EditPanel
            parsedText={parsedText}
            setParsedText={setParsedText}
            onBack={() => setStep("select")}
            onGenerateFinal={handleGenerateFinalScript}
            finalLoading={finalLoading}
            onClose={onClose}
            generatedScript={generatedScript}
            handleAddScriptInPageInput={handleAddScriptInPageInput}
          />
        )}
      </Box>
    </Modal>
  );
};

const EditPanel = ({
  parsedText,
  setParsedText,
  onBack,
  onGenerateFinal,
  finalLoading,
  onClose,
  generatedScript,
  handleAddScriptInPageInput
}) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      p: 3,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    {/* Header */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="h4" fontWeight="bold">
        Write Script
      </Typography>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Two Columns */}
    <Box sx={{ display: {sm: 'block', md:"flex"}, flexGrow: 1, gap: 2, overflow: {sm: "scroll", md: "hidden"} }}>
      {/* Left Column: User Input */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #ccc",
          borderRadius: 2,
          bgcolor: "#fff",
          overflow: "hidden",
          mb: {sm: 3, md: 0},
        }}
      >
        {/* Scrollable Text Input */}
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              // p: 2,
            }}
          >
            <TextField
              fullWidth
              multiline
              value={parsedText}
              onChange={(e) => setParsedText(e.target.value)}
              placeholder="Type your script here and press 'Generate Final Script' when done..."
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                width: "100%",
                fontSize: "1rem",
                "& .MuiInputBase-input": {
                  padding: "12px",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none !important",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none !important",
                },
                "&:hover .MuiInput-underline:before": {
                  borderBottom: "none !important",
                },
                "&.Mui-focused .MuiInput-underline:after": {
                  borderBottom: "none !important",
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer Buttons */}
        <Box
          p={2}
          borderTop="1px solid #ddd"
          display="flex"
          justifyContent="flex-end"
          gap={2}
        >
          <Button variant="outlined" onClick={onBack}>
            ← Back
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={onGenerateFinal}
            sx={{ maxWidth: "300px", width: "100%" }}
            disabled={finalLoading}
          >
            {finalLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Generate Final Script"
            )}
          </Button>
        </Box>
      </Box>

      {/* Right Column: Generated Script */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #ccc",
          borderRadius: 2,
          bgcolor: "#f9f9f9",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box p={2} borderBottom="1px solid #ddd">
          <Typography variant="h5" fontWeight="bold">
            Generated Script:
          </Typography>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <Box
            sx={{
              height: "100%",
              overflowY: "auto",
              p: 2,
              fontSize: "14px",
              whiteSpace: "pre-wrap",
            }}
          >
            {generatedScript && generatedScript.length > 0 ? (
              <>
                {generatedScript.map((line, index) => (
                  <Box key={index} mb={1}>
                    <Typography
                      variant="body1"
                      sx={{ whiteSpace: "pre-wrap", fontWeight: "bold" }}
                    >
                      {line.topicNumber}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {line.text}
                    </Typography>
                    <br />
                  </Box>
                ))}
              </>
            ) : (
              <Typography variant="body1">No script generated yet.</Typography>
            )}
          </Box>
        </Box>

        {/* Footer Button */}
        <Box
          p={2}
          borderTop="1px solid #ddd"
          display="flex"
          justifyContent="flex-end"
          onClick={()=>{handleAddScriptInPageInput(generatedScript); }}
        >
          <Button variant="contained" onClick={onClose}>
            Add to Project
          </Button>
        </Box>
      </Box>
    </Box>
  </Box>
);



const LeftPanel = ({
  newsletters,
  selectedNewsletters,
  onToggle,
  onGenerate,
  previewText,
  onPreview,
  handleDownloadAttachment,
  onResumeEdit,
  isResumeAvailable,
  promptLoading,
  onClose
}) => (
  <Box sx={{ width: "100%", p: 3 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>

      <Typography variant="h4" fontWeight="bold">
        Select Newsletters
      </Typography>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
      
    </Box>
    <Box sx={{ display: {sm: 'block', md:"flex"}, height: "calc(100% - 48px)", overflowY: {sm: "scroll", md: "hidden"} }}>
      {/* Left Column: Newsletters */}
      <Box sx={{ width: {sm: '100%', md:"45%"} , pr: 2, overflowY: "auto", mb: {sm: 2, md: 0} }}>
        <List dense>
          {newsletters.map((newsletter) => (
            <Box key={newsletter._id}>
              <ListItem disablePadding>
                <Checkbox
                  checked={selectedNewsletters.includes(newsletter._id)}
                  onChange={() => onToggle(newsletter._id)}
                />
                <ListItemText
                  primary={newsletter.subject || "N/A"}
                  secondary={`Date: ${format(new Date(newsletter.receivedAt), "PPP p") || "N/A"}`}
                />
                <ListItemSecondaryAction sx={{ mr: 3 }}>
                  {newsletter.attachments?.map((file, i) => (
                    <IconButton
                      key={i}
                      onClick={() => handleDownloadAttachment(file)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <DownloadIcon />
                    </IconButton>
                  ))}
                </ListItemSecondaryAction>
                <IconButton onClick={() => onPreview(newsletter?.body?.text)}>
                  <PreviewIcon />
                </IconButton>
              </ListItem>
              <Divider />
            </Box>
          ))}
        </List>
        <Box mt={3} display="flex" gap={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onGenerate}
            disabled={selectedNewsletters.length === 0 || promptLoading}
          >
            {/* Generate Script */}
            {/* Extract Script from Selection */}
            {promptLoading ? <>
              <CircularProgress size={22} color="inherit" /> &nbsp; Using Newsletter(s)... </>  : "Use Newsletter(s)"}
          </Button>
          
          <Button variant="outlined" onClick={onResumeEdit}>
            {isResumeAvailable ? 'Resume Editing': 'Skip'}
          </Button>
        </Box>
      </Box>

      {/* Right Column: Preview */}
      <Box sx={{ 
        width: {sm: '100%', md:"55%"}, pl: 2, display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        mb: {sm: 2, md: 0},
      }}>
        
        <>
          <Typography variant="h5" fontWeight="bold" mb={1}>
            Preview:
          </Typography>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: 2,
              border: "1px solid #ccc",
              borderRadius: 2,
              bgcolor: "#f9f9f9",
              fontSize: "14px",
              whiteSpace: "pre-wrap",
            }}
          >
            {previewText}
          </Box>
        </>
      </Box>
    </Box>
  </Box>
);

export default NewsletterScriptModal;